import React, { type ReactElement, useCallback, useState } from 'react';
import { JsonParam, useQueryParam } from 'use-query-params';
import PrintPageBreaker from 'atom-components/print-page-breaker';
import {
  transformEntryFilters,
  transformFilters
} from 'pages/clusters/helpers/aggregated-report.helpers';
import ClusterPageLayout from 'pages/clusters/layouts/cluster-page-layout';
import ClusterReportNavigator from 'pages/clusters/subpages/aggregated-reports-tab/cluster-report-navigator';
import FilterTopbar from 'pages/clusters/subpages/aggregated-reports-tab/filter-topbar';
import type { Filters, FormValues } from 'pages/clusters/types';
import { FilterSummary } from 'pages/reports/components/shared-atom-components/filters';
import { useClusterResponses } from 'services/assist-api/entities/test-instance-response/reports/customer-group/cluster-report-responses';
import { isInitialLoad } from 'utils/check-query-status';

/**
 * Displays filters, and report navigation for aggregate styled cluster reports.
 * These reports have anonymised user data from the API, and so rely on the API to handle filtering.
 */
export default function AggregatedReportsTab(): ReactElement {
  const [filters] = useQueryParam<Filters | undefined>('filters', JsonParam);
  const [entryFilters, setEntryFilters] = useQueryParam<FormValues>('entry', JsonParam);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const isReadyToShowReport = isSubmitted && !!entryFilters;
  const queryResponses = useClusterResponses(
    { filters: { ...transformEntryFilters(entryFilters), ...transformFilters(filters) } },
    { enabled: isReadyToShowReport }
  );

  const handleEntryFilterSubmission = useCallback(
    (values: FormValues) => {
      if (values === entryFilters) queryResponses.refetch();
      else setEntryFilters(values);

      setIsSubmitted(true);
    },
    [entryFilters, queryResponses, setEntryFilters]
  );

  return (
    <ClusterPageLayout>
      <div className="card mb-2">
        <h4 className="card-header">Aggregated Cluster Reports</h4>
        <div className="card-body">
          <p>
            Welcome to cluster reporting in NZCER Assist. We are pleased to provide cluster
            functionality and reporting for our PAT and STAR products.
          </p>
          <p>
            When joining a cluster, schools decide what PAT and STAR data they will share. Those
            choices will be reflected in the report generation options below, and inform what data
            and reports you can access. If you cannot see the subjects you are expecting under the
            Area field, please contact our team at:{' '}
            <a href="mailto:assessmentservices@nzcer.org.nz?subject=Assist: Cluster Areas">
              assessmentservices@nzcer.org.nz
            </a>{' '}
            so we can review your cluster settings.
          </p>
          <p>
            Cluster reporting is similar to school-level reporting in Assist, with two key
            differences. Cluster-level reporting <u>does not identify</u> individual students, and
            users with the Cluster Admin role can filter by organisation/school instead of filtering
            data by class.
          </p>
          <p>
            Year level, Stanine, and Item reports can be generated at cluster level. Please note
            that Item reporting is only available when selecting a <u>single</u> test.
          </p>
          <p>
            If you have any questions regarding cluster reporting, please contact our Assessment
            Services team.
          </p>
        </div>
        <hr />
        <FilterTopbar
          initialValues={entryFilters}
          onSubmit={handleEntryFilterSubmission}
          isLoading={isInitialLoad(queryResponses)}
        />
      </div>
      <FilterSummary
        className="d-none d-print-block"
        filters={[
          {
            label: 'Organisation',
            value: filters?.clusterCustomers?.map(({ customer }) => customer.name).join(', ')
          },
          {
            label: 'Gender',
            value: filters?.genders?.map(({ name }) => name).join(', ')
          },
          {
            label: 'Ethnicity',
            value: filters?.ethnicities?.map(({ name }) => name).join(', ')
          },
          {
            label: 'Year Level',
            value: filters?.levels?.map(({ name }) => name).join(', ')
          }
        ]}
      />
      <PrintPageBreaker />

      {isReadyToShowReport && (
        <ClusterReportNavigator
          areaId={entryFilters?.area}
          testIds={entryFilters?.test}
          period={entryFilters?.period}
          queryResponses={queryResponses}
        />
      )}
    </ClusterPageLayout>
  );
}
